import { UntitledIcon } from '@faceup/icons'
import { ulChevronDown } from '@faceup/icons/ulChevronDown'
import { ulGlobe02 } from '@faceup/icons/ulGlobe02'
import { twMerge, UnstyledButton } from '@faceup/ui'
import { Dropdown, Typography } from '@faceup/ui-base'
import type { Language } from '@faceup/utils'
import { type ComponentProps, type ForwardedRef, forwardRef } from 'react'
import { useLanguageName } from '../hooks'

type LanguageDropdownProps = {
  languages: Language[]
  activeLanguage: Language
  areLanguagesNative?: boolean
  className?: string
  onChangeLanguage: (language: Language) => void
  styles?: { targetText?: Omit<ComponentProps<typeof Typography.Text>, 'children'> }
}

export const LanguageDropdown = forwardRef(
  (
    {
      languages,
      activeLanguage,
      onChangeLanguage,
      areLanguagesNative = false,
      className,
      styles,
    }: LanguageDropdownProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const { getLanguageName, getLanguageList } = useLanguageName()

    return (
      <Dropdown
        menu={{
          style: {
            maxHeight: '50vh',
          },
          items: getLanguageList({ specificLanguages: languages }).map(language => ({
            key: language.language,
            label: areLanguagesNative ? language.nativeName : language.translatedName,
            onClick: () => onChangeLanguage(language.language),
          })),
        }}
      >
        <UnstyledButton
          ref={ref}
          className={twMerge('flex items-center gap-2', className)}
          data-test='language-dropdown'
        >
          <Typography.Text {...styles?.targetText}>
            <div className='flex items-center gap-2'>
              <UntitledIcon icon={ulGlobe02} />
              {getLanguageName({
                language: activeLanguage,
                type: areLanguagesNative ? 'native' : 'translated',
              })}
              <UntitledIcon icon={ulChevronDown} size={18} />
            </div>
          </Typography.Text>
        </UnstyledButton>
      </Dropdown>
    )
  }
)
